import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, retry, takeUntil } from 'rxjs/operators';
import {environment} from "../environments/environment";
import { AuthStatusService } from './shared/services/auth-status.service';
import { LoggerService } from '../app/shared/services/logger.service';
import { RequestCancellationService } from './shared/services/request-cancellation.service';
@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {
  constructor(
    private authStatusService: AuthStatusService,
    private logger: LoggerService,
    private requestCancellationService: RequestCancellationService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const responseType = request.params.get('responseType');
    const type = request.urlWithParams.search(/type=formdata/) > -1;

    // Apply Bearer token or other headers
    request = this.addBearerAuthHeader(request);
    request = this.handleRequestMethod(request, type);

    // Handle POST, PUT or other request methods
    const isLogEndpoint = request.url.includes('/logs-certiplate-ui-v3');
    const isLogoutRequest = request.url.includes('/logout');
    const startTime = Date.now();

    // if (!isLogEndpoint) {
    //   this.logger.info(`[UI Request Start] ${request.method} ${request.url}`, request.body);
    // }


    // Wait for token refresh to complete before making the request
    const request$ = new Observable<HttpEvent<any>>((observer) => {
      this.authStatusService.isRefreshingToken$.subscribe((isRefreshing) => {
        if (isRefreshing) {
          // If the token is being refreshed, wait for the refresh to complete
          return; // No further action, stay in the loop until refresh is done
        }

        // Apply takeUntil directly on next.handle so Angular aborts the actual browser XHR.
        // Logout requests are excluded so they always go through even during cancellation.
        const httpRequest$ = isLogoutRequest
          ? next.handle(request)
          : next.handle(request).pipe(takeUntil(this.requestCancellationService.cancel$));

        httpRequest$.subscribe(
          (event) => {
            if (event instanceof HttpResponse && !isLogEndpoint) {
              const duration = Date.now() - startTime;
              // this.logger.info(`[UI Request Success] ${request.method} ${request.url} [Status: ${event.status}] - ${duration}ms`);
            }
            observer.next(event);  // Forward the event
          },
          (error) => {
            if (!isLogEndpoint) {
              const duration = Date.now() - startTime;
              this.logger.error(`[UI Request Error] ${request.method} ${request.url} - ${duration}ms`, error);
            }
            observer.error(error);  // Handle errors
          },
          () => {
            observer.complete();  // Complete the observable
          }
        );
      });
    }).pipe(
      retry(1),  // Retry once in case of failure
      catchError((returnedError) => {
        console.error('[Interceptor] HTTP error received:', returnedError);
        if (returnedError && returnedError.error) {
          console.error('[Interceptor] Error body:', returnedError.error);
        }
        return this.handleServerSideError(returnedError);  // Handle errors from the server side
      })
    );

    return request$;
  }



  // Method to add Bearer Authentication header
  // private addBearerAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
  //   const token = localStorage.getItem('JwtToken');
  //   const authHeader = token ? `Bearer ${token}` : '';
  //   return request.clone({
  //     setHeaders: {
  //       Authorization: authHeader,
  //       'Content-Type': 'application/json',
  //     },
  //   });
  // }
  private addBearerAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
    if (request.headers.has('Authorization') || request.url.includes('freshdesk.com')) {
      return request;
    }

    let token = '';

    // Use Directus token only for Directus API requests
    if (request.url.includes(environment.DIRECTUS_API_URL)) {
      token = environment.DIRECTUS_API_TOKEN;
    } else {
      // Use stored JWT token for other requests
      token = localStorage.getItem('JwtToken') || '';
    }

    const authHeader = token ? `Bearer ${token}` : '';
    const headers: any = { Authorization: authHeader };
    if (!(request.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    return request.clone({ setHeaders: headers });
  }

  // Method to handle POST/PUT requests and add body or headers
  private handleRequestMethod(
    request: HttpRequest<any>,
    isFormData: boolean
  ): HttpRequest<any> {
    if (request.url.includes('freshdesk.com')) {
      return request;
    }

    if (request.method === 'POST' || request.method === 'PUT') {
      if (isFormData) {
        return request.clone({
          setHeaders: {
            'Content-type': 'multipart/form-data',
          },
          body: request.body,
        });
      } else {
        return request.clone({
          body: {
            ...request.body,
            requestHead: {
              appName: 'Certiplate UI',
              platform: 'web',
            },
          },
        });
      }
    }
    return request;
  }

  // Method to handle error responses
  private handleServerSideError(returnedError: any): Observable<any> {
    let errorMessage: String;
    if (returnedError.error instanceof ErrorEvent) {
      errorMessage = `Error: ${returnedError.error.message}`;
    } else if (returnedError instanceof HttpErrorResponse) {
      switch (returnedError.status) {
        case 401:
          errorMessage =
            'Unauthorized Access, Logged out. Please Re-login Again';
          this.clearStorage();
          break;
        case 0:
          errorMessage =
            'Backend APIs are not responding, Please try again later';
          break;
        default: {
          errorMessage = `Error Status ${returnedError.status}: ${returnedError?.error?.responseHead?.statusCode} - ${returnedError?.error?.responseHead?.statusDesc}`;
          const specificMessage = returnedError?.error?.responseBody?.message;
          if (specificMessage && typeof specificMessage === 'string') {
            errorMessage = `${errorMessage} — ${specificMessage}`;
          }
        }
      }
    }
    return throwError(errorMessage);
  }

  // Method to clear storage on logout
  private clearStorage() {
    localStorage.removeItem('JwtToken');
  }
}
